import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Table from "src/componets/Table";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { ButtonEdit } from "src/componets/Buttons";
import { setEditId, UserInterface } from "src/store/callrecorder/usersSlice";
import { selectBundleEntities } from "src/store/callrecorder/bundlesSlice";
import { selectUserNumberEntities } from "src/store/callrecorder/userNumbersSlice";
import { fetchUsers } from "src/actions/callrecorder/usersActions";
import { queryParams } from "src/helpers/UrlHelpers";
import { EntityId } from "@reduxjs/toolkit";
import usePermissions from "src/hooks/usePermissions";
import { formattedDate } from "src/helpers/DateHelpers";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";
import PlatformIcon from "../../_partials/PlatformIcon";

import { Space, Tag } from "antd";

interface ListTableInterface {
  className?: string;
  limit?: number;
  filterParams?: object;
}

const _ListTable = ({ className, limit = 42, filterParams }: ListTableInterface) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const currentPage = queryParams.getInt(searchParams, 'page') || 1;
  const urlTerm = searchParams.get('term') || '';
  const urlSearchBy = searchParams.get('search_by') || '';
  const urlOrderBy = searchParams.get('order_by') || '';
  const urlOrder = searchParams.get('order') || '';

  const params = useMemo(() => ({
    limit,
    offset: (currentPage - 1) * limit,
    ...filterParams,
    ...(urlTerm ? { term: urlTerm, search_by: urlSearchBy } : {}),
    ...(urlOrderBy ? { order_by: urlOrderBy, order: urlOrder } : {})
  }), [limit, currentPage, filterParams, urlTerm, urlSearchBy, urlOrderBy, urlOrder]);

  useEffect(() => {
    if (filterParams) {
      dispatch(fetchUsers(params));
    }
  }, [params, filterParams, dispatch]);

  const ids = useAppSelector(state => state.callrecorder.users.ids);
  const entities = useAppSelector(state => state.callrecorder.users.entities);
  const count = useAppSelector(state => state.callrecorder.users.count);
  const bundleEntities = useAppSelector(selectBundleEntities);
  const userNumberEntities = useAppSelector(selectUserNumberEntities);
  const { write_callrecorder } = usePermissions();
  const data = ids ? ids.map(id => entities[id]) : [];

  function onClickEdit(id: EntityId) {
    dispatch(setEditId(id));
  }

  const boolTag = (val: boolean) =>
    val ? <Tag color="success">yes</Tag> : <Tag color="default">no</Tag>;

  const columns: ColumnsType<UserInterface> = [
    {
      key: 'id',
      title: 'ID',
      render: (_, { id, url }) => (
        <OptionalLink link={url}>
          {`#${id}`}
        </OptionalLink>
      ),
      width: 40
    },

    {
      key: 'bundle',
      title: 'Bundle',
      render: (_, { bundle }) => {
        const b = bundleEntities[bundle];
        if (!b) return '—';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ display: 'inline-block', minWidth: '13px' }}>
              <PlatformIcon platform={b.platform} />
            </span>
            <OptionalLink link={b.url}>{b.name}</OptionalLink>
          </span>
        );
      },
      width: 120
    },

    {
      key: 'user_number',
      title: 'Src',
      render: (_, { user_number }) => {
        const un = user_number ? userNumberEntities[user_number] : null;
        return un ? <small>+{un.number}</small> : '—';
      },
      width: 140
    },

    {
      key: 'cnumber',
      title: 'Dst',
      render: (_, { cnumber }) => <small>{cnumber}</small>,
      width: 140
    },

    {
      key: 'balance',
      title: 'Balance',
      render: (_, { balance }) => <small>{balance}</small>,
      width: 80
    },

    {
      key: 'verified',
      title: 'Verified',
      render: (_, { verified }) => boolTag(verified),
      width: 80
    },

    {
      key: 'suspicious',
      title: 'Suspicious',
      render: (_, { suspicious }) => suspicious ? <Tag color="error">yes</Tag> : <Tag color="default">no</Tag>,
      width: 90
    },

    {
      key: 'updated_at',
      title: 'Updated',
      render: (_, { updated_at }) => <small>{formattedDate(updated_at).dateTime}</small>,
      width: 135
    },

    ...(write_callrecorder ? [{
      key: 'action',
      title: 'Action',
      render: (_: any, { id }: UserInterface) => {
        return (
          <Space size="middle">
            <ButtonEdit key={'button-edit'} onClick={() => onClickEdit(id)} />
          </Space>
        );
      },
      width: 80
    }] : []),
  ];

  return (
    <div className={className}>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
        search_keys={['id', 'bundle', 'user_number', 'cnumber']}
        sort_keys={['id', 'balance', 'verified', 'suspicious', 'updated_at']}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
